import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, RotateCcw, Check, AlertTriangle, Share2, Copy, Info, Compass, ArrowRight, ShieldCheck, ShieldAlert, Download, Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { scrollToElement, scrollToElementAfterRender, focusWithoutScroll } from "@/lib/scroll";

// Import types and functions strictly from the shared batch library
import { type SelvesInput, type SelvesState, type AlternateSelf, type InterestId, type StrengthId, type AmbitionId, type LifePathId, INTERESTS, STRENGTHS, AMBITIONS, LIFE_PATHS, MAX_INTERESTS, MAX_STRENGTHS, NICKNAME_MAX, FICTION_NOTICE, PRIVACY_NOTE, emptyInput, displayName, sanitizeNickname, randomInput, randomVariant, randomVariants, selvesOverlap, selfFingerprint, encodeState, decodeState, generateSelves, buildSelfShareText, buildAllShareText, parseUniverseParam, variationIdFor, ENGINE_VERSION } from "@/lib/infiniteSelves";

// Library storage
import { saveToLibrary } from "@/lib/infiniteSelves/library";

// Universe physics model
import { type UniverseConfig, type UniverseProfile, DEFAULT_CONFIG, randomConfig, generateUniverse, configsEqual } from "@/lib/universe";

// Sibling presentational components
import SelfCard from "../components/SelfCard";
import SelfDetail from "../components/SelfDetail";
import CompareView from "../components/CompareView";
import SavedPossibilities from "../components/SavedPossibilities";

// Dream Bridge store & actions
import { bridgeActions, getDossierChapter, useBridgeStore } from "@/lib/dreamBridge";
import { AXIS_ORDER, type AxisId } from "@/lib/infiniteSelves";
import { Checkbox } from "@/components/ui/checkbox";

// Local clipboard helper
import { copyText } from "./clipboard";

/**
 * Compare two SelvesInput objects for value equality.
 */
function inputsEqual(a: SelvesInput, b: SelvesInput): boolean {
  if (a.nickname !== b.nickname) return false;
  if (a.ambition !== b.ambition) return false;
  if (a.lifePath !== b.lifePath) return false;
  if (a.interests.length !== b.interests.length) return false;
  for (let i = 0; i < a.interests.length; i++) {
    if (a.interests[i] !== b.interests[i]) return false;
  }
  if (a.strengths.length !== b.strengths.length) return false;
  for (let i = 0; i < a.strengths.length; i++) {
    if (a.strengths[i] !== b.strengths[i]) return false;
  }
  return true;
}

/**
 * Safely execute a non-essential side effect without failing generation.
 */
function safely(label: string, fn: () => void): void {
  try {
    fn();
  } catch (err) {
    console.error(`${label} failed`, err);
  }
}
export default function SelfStudio() {
  // --- Form Interactive State ---
  const [formInput, setFormInput] = useState<SelvesInput>(emptyInput);
  const [universeMode, setUniverseMode] = useState<"separate" | "shared">("separate");
  const [sharedUniverseConfig, setSharedUniverseConfig] = useState<UniverseConfig>(DEFAULT_CONFIG);
  const [customArrivalUniverse, setCustomArrivalUniverse] = useState<{
    config: UniverseConfig;
    profile: UniverseProfile;
  } | null>(null);

  // --- Committed Simulation State ---
  const [committedState, setCommittedState] = useState<SelvesState | null>(null);
  const [selves, setSelves] = useState<AlternateSelf[] | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [generationErrorDetail, setGenerationErrorDetail] = useState<string | null>(null);
  const [pdfGenerating, setPdfGenerating] = useState<boolean>(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // --- UI Interactivity ---
  const [exploringIndex, setExploringIndex] = useState<number | null>(null);
  const [compareSelection, setCompareSelection] = useState<number[]>([]); // indexes in selves (up to 2)

  // Feedback notifications & fallbacks
  const [feedback, setFeedback] = useState<{
    message: string;
    success: boolean;
  } | null>(null);
  const [urlNotice, setUrlNotice] = useState<string | null>(null);
  const [copyFallbackText, setCopyFallbackText] = useState<string | null>(null);
  const [shareFallbackUrl, setShareFallbackUrl] = useState<string | null>(null);

  // Refs for scrolling and timeout cleanups
  const feedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isGeneratingRef = useRef<boolean>(false);
  const isMountedRef = useRef<boolean>(true);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const detailRef = useRef<HTMLDivElement | null>(null);
  const compareRef = useRef<HTMLDivElement | null>(null);

  // Transient random universe generated for the "shared" dropdown option
  const [storedRandomUniverse, setStoredRandomUniverse] = useState<UniverseConfig | null>(null);

  // Keep isGeneratingRef in sync
  useEffect(() => {
    isGeneratingRef.current = isGenerating;
  }, [isGenerating]);

  // Clean up timers on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
      if (generationTimerRef.current) clearTimeout(generationTimerRef.current);
    };
  }, []);

  // Central generation runner: yields to browser so loading UI paints, handles errors gracefully
  const runGeneration = useCallback((_label: string, work: () => void) => {
    if (isGeneratingRef.current) return;
    if (generationTimerRef.current) {
      clearTimeout(generationTimerRef.current);
    }
    setGenerationError(null);
    setGenerationErrorDetail(null);
    setIsGenerating(true);
    isGeneratingRef.current = true;
    generationTimerRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      try {
        work();
      } catch (err) {
        console.error("Failed to generate selves:", err instanceof Error ? err.message : String(err), err);
        if (isMountedRef.current) {
          setGenerationError("We couldn't generate your four selves this time. Your answers are kept. Please try again.");
          const rawMsg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
          setGenerationErrorDetail(rawMsg.slice(0, 200));
        }
      } finally {
        if (isMountedRef.current) {
          setIsGenerating(false);
          isGeneratingRef.current = false;
        }
      }
    }, 40);
  }, []);
  const nicknameValidation = useMemo(() => {
    return sanitizeNickname(formInput.nickname);
  }, [formInput.nickname]);
  const isFormValid = nicknameValidation.error === null;

  // Computed variation ID for current committed state
  const currentVariationId = useMemo(() => {
    if (!committedState) return "";
    return variationIdFor(committedState);
  }, [committedState]);

  // Show feedback alert toast
  const showFeedback = useCallback((message: string, success: boolean) => {
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
    }
    setFeedback({
      message,
      success
    });
    feedbackTimeoutRef.current = setTimeout(() => {
      setFeedback(null);
    }, 3800);
  }, []);

  // Compute canonical share link for a state (never includes order by default per spec)
  const getShareUrl = useCallback((stateToEncode: SelvesState): string => {
    const encoded = encodeState(stateToEncode);
    if (typeof window !== "undefined") {
      return `${window.location.origin}${window.location.pathname}?self=${encoded}#selves-studio`;
    }
    return `?self=${encoded}#selves-studio`;
  }, []);

  // Update address bar silently
  const updateAddressBar = useCallback((stateToEncode: SelvesState, orderId?: string | null) => {
    try {
      if (typeof window !== "undefined") {
        const encoded = encodeState(stateToEncode);
        const orderSuffix = orderId ? `&order=${encodeURIComponent(orderId)}` : "";
        const newUrl = `${window.location.pathname}?self=${encoded}${orderSuffix}#selves-studio`;
        window.history.replaceState(null, "", newUrl);
      }
    } catch {
      // Ignored if sandboxed iframe restricts history
    }
  }, []);

  // Reset address bar to clean state
  const resetAddressBar = useCallback(() => {
    try {
      if (typeof window !== "undefined") {
        const cleanUrl = `${window.location.pathname}#selves-studio`;
        window.history.replaceState(null, "", cleanUrl);
      }
    } catch {
      // Ignored
    }
  }, []);

  // Check if form currently differs from committed results
  const hasSettingsChanged = useMemo(() => {
    if (!committedState) return false;
    if (!inputsEqual(formInput, committedState.input)) return true;
    const committedMode = committedState.universe === null ? "separate" : "shared";
    if (universeMode !== committedMode) return true;
    if (universeMode === "shared" && committedState.universe) {
      if (!configsEqual(sharedUniverseConfig, committedState.universe)) return true;
    }
    return false;
  }, [formInput, universeMode, sharedUniverseConfig, committedState]);

  // Profile of current shared universe selection for preview
  const currentSharedProfile = useMemo(() => {
    if (universeMode !== "shared") return null;
    return generateUniverse(sharedUniverseConfig);
  }, [universeMode, sharedUniverseConfig]);

  // Helper to record an entry into the local library
  const recordToLibrary = useCallback((stateToRecord: SelvesState) => {
    try {
      const code = encodeState(stateToRecord);
      const varId = variationIdFor(stateToRecord);
      const uMode: "separate" | "shared" = stateToRecord.universe ? "shared" : "separate";
      saveToLibrary({
        code,
        savedAt: Date.now(),
        variationId: varId,
        nickname: stateToRecord.input.nickname,
        universeMode: uMode
      });
    } catch {
      // Ignore
    }
  }, []);

  // Load from URL (mount & popstate)
  const loadFromUrl = useCallback(() => {
    try {
      if (typeof window === "undefined") return;
      const searchParams = new URLSearchParams(window.location.search);
      const sParam = searchParams.get("self") || searchParams.get("s");
      const orderParam = searchParams.get("order");
      if (sParam) {
        const decoded = decodeState(sParam);
        if (decoded) {
          setFormInput(decoded.input);
          if (decoded.universe) {
            setUniverseMode("shared");
            setSharedUniverseConfig(decoded.universe);
          } else {
            setUniverseMode("separate");
          }
          setCommittedState(decoded);
          try {
            const computedSelves = generateSelves(decoded);
            setSelves(computedSelves);
            setGenerationError(null);
            setGenerationErrorDetail(null);
          } catch (err) {
            console.error("Failed to generate selves from URL state:", err instanceof Error ? err.message : String(err), err);
            setGenerationError("We couldn't generate your four selves this time. Your answers are kept. Please try again.");
            const rawMsg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
            setGenerationErrorDetail(rawMsg.slice(0, 200));
            return;
          }
          setExploringIndex(null);
          setCompareSelection([]);
          setCopyFallbackText(null);
          setShareFallbackUrl(null);
          setUrlNotice(null);

          // Non-essential extras wrapped safely
          safely("loadFromUrl.recordToLibrary", () => {
            recordToLibrary(decoded);
          });
          return;
        }
        // Failure: show required error and load empty form
        setUrlNotice("That link couldn't be read, so you're starting fresh.");
        setFormInput(emptyInput());
        setUniverseMode("separate");
        setCommittedState(null);
        setSelves(null);
        return;
      }

      // Check ?u parameter from Create Your Universe
      const uParam = searchParams.get("u");
      if (uParam) {
        const universeFromParam = parseUniverseParam(uParam);
        if (universeFromParam) {
          const profile = generateUniverse(universeFromParam);
          setCustomArrivalUniverse({
            config: universeFromParam,
            profile
          });
          setUniverseMode("shared");
          setSharedUniverseConfig(universeFromParam);
          setUrlNotice(`Loaded the universe you designed: ${profile.name}. Reveal your selves to place them inside it.`);
        } else {
          setUrlNotice("That universe link couldn't be read.");
        }
      }
    } catch {
      // Quiet fail safe
    }
  }, [recordToLibrary]);
  useEffect(() => {
    loadFromUrl();
    const handlePopState = () => {
      loadFromUrl();
    };
    if (typeof window !== "undefined") {
      window.addEventListener("popstate", handlePopState);
      return () => {
        window.removeEventListener("popstate", handlePopState);
      };
    }
  }, [loadFromUrl]);

  // Load a specific code from SavedPossibilities or bookmarks
  const handleSelectCode = useCallback((code: string) => {
    const decoded = decodeState(code);
    if (decoded) {
      setFormInput(decoded.input);
      if (decoded.universe) {
        setUniverseMode("shared");
        setSharedUniverseConfig(decoded.universe);
      } else {
        setUniverseMode("separate");
      }
      setCommittedState(decoded);
      try {
        const computed = generateSelves(decoded);
        setSelves(computed);
        setGenerationError(null);
        setGenerationErrorDetail(null);
      } catch (err) {
        console.error("Failed to generate selves from code:", err instanceof Error ? err.message : String(err), err);
        setGenerationError("We couldn't generate your four selves this time. Your answers are kept. Please try again.");
        const rawMsg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
        setGenerationErrorDetail(rawMsg.slice(0, 200));
        return;
      }
      setExploringIndex(null);
      setCompareSelection([]);
      setCopyFallbackText(null);
      setShareFallbackUrl(null);

      // Non-essential extras wrapped safely
      safely("handleSelectCode.recordToLibrary", () => {
        recordToLibrary(decoded);
      });
      updateAddressBar(decoded);

      // Scroll to results via safe helper (only if not already comfortably in view)
      scrollToElementAfterRender(() => resultsRef.current, {
        block: "start",
        onlyIfNeeded: true
      });
    }
  }, [recordToLibrary, updateAddressBar]);

  // --- Actions ---

  // Reveal my four selves (Primary action)
  const handleReveal = useCallback(() => {
    if (!isFormValid) return;
    const sanitizedNicknameVal = nicknameValidation.value;
    const finalInput: SelvesInput = {
      ...formInput,
      nickname: sanitizedNicknameVal
    };

    // Each reveal draws fresh random variants
    const freshVariants = randomVariants();
    const nextState: SelvesState = {
      input: finalInput,
      variants: freshVariants,
      universe: universeMode === "shared" ? sharedUniverseConfig : null
    };
    runGeneration("reveal", () => {
      const computed = generateSelves(nextState);
      setCommittedState(nextState);
      setSelves(computed);
      setExploringIndex(null);
      setCompareSelection([]);
      setCopyFallbackText(null);
      setShareFallbackUrl(null);

      // Auto-save to library safely
      safely("handleReveal.recordToLibrary", () => {
        recordToLibrary(nextState);
      });
      safely("handleReveal.updateAddressBar", () => {
        updateAddressBar(nextState);
      });

      // Scroll to results only after successful generation if not already comfortably visible
      scrollToElementAfterRender(() => resultsRef.current, {
        block: "start",
        onlyIfNeeded: true
      });
    });
  }, [isFormValid, nicknameValidation.value, formInput, universeMode, sharedUniverseConfig, runGeneration, recordToLibrary, updateAddressBar]);

  // Explore Another Possibility (keeps answers + universe mode, draws fresh variants with overlap retry)
  const handleExploreAnotherPossibility = useCallback(() => {
    if (!committedState || !selves) return;
    runGeneration("explore-another", () => {
      let nextVariants = randomVariants();
      let nextState: SelvesState = {
        ...committedState,
        variants: nextVariants
      };
      let computed = generateSelves(nextState);

      // Retry up to 8 times until no overlap with previous selves
      for (let attempt = 0; attempt < 8; attempt++) {
        if (!selvesOverlap(selves, computed)) {
          break;
        }
        nextVariants = randomVariants();
        nextState = {
          ...committedState,
          variants: nextVariants
        };
        computed = generateSelves(nextState);
      }
      setCommittedState(nextState);
      setSelves(computed);

      // Non-essential extras wrapped safely
      safely("handleExploreAnotherPossibility.recordToLibrary", () => {
        recordToLibrary(nextState);
      });
      safely("handleExploreAnotherPossibility.updateAddressBar", () => {
        updateAddressBar(nextState);
      });
      safely("handleExploreAnotherPossibility.feedback", () => {
        showFeedback("Explored another possibility — synthesized 4 new timelines", true);
      });
    });
  }, [committedState, selves, runGeneration, recordToLibrary, updateAddressBar, showFeedback]);

  // Per-card timeline regenerate (retry up to 8 times until fingerprint shares no item with current four selves)
  const handleRegenerateCard = useCallback((index: number) => {
    if (!committedState || !selves) return;
    runGeneration(`regenerate-card-${index}`, () => {
      // Extract existing fingerprints across all 4 current selves
      const currentFingerprintItems = new Set<string>();
      for (const s of selves) {
        for (const item of selfFingerprint(s)) {
          if (item) currentFingerprintItems.add(item);
        }
      }
      let bestVariants: [number, number, number, number] = [...committedState.variants];
      let bestComputed = selves;
      for (let attempt = 0; attempt < 8; attempt++) {
        const candidateVariants: [number, number, number, number] = [...committedState.variants];
        candidateVariants[index] = randomVariant();
        const candidateState: SelvesState = {
          ...committedState,
          variants: candidateVariants
        };
        const candidateComputed = generateSelves(candidateState);
        const newSelf = candidateComputed[index];
        const newFingerprint = selfFingerprint(newSelf);

        // Check if any item in newFingerprint exists in currentFingerprintItems
        const hasCollision = newFingerprint.some(item => item && currentFingerprintItems.has(item));
        bestVariants = candidateVariants;
        bestComputed = candidateComputed;
        if (!hasCollision) {
          break;
        }
      }
      const finalState: SelvesState = {
        ...committedState,
        variants: bestVariants
      };
      setCommittedState(finalState);
      setSelves(bestComputed);

      // Non-essential extras wrapped safely
      safely(`handleRegenerateCard-${index}.recordToLibrary`, () => {
        recordToLibrary(finalState);
      });
      safely(`handleRegenerateCard-${index}.updateAddressBar`, () => {
        updateAddressBar(finalState);
      });
      safely(`handleRegenerateCard-${index}.feedback`, () => {
        showFeedback(`Regenerated timeline for ${bestComputed[index].name}`, true);
      });
    });
  }, [committedState, selves, runGeneration, recordToLibrary, updateAddressBar, showFeedback]);

  // Start over
  const handleStartOver = useCallback(() => {
    setFormInput(emptyInput());
    setUniverseMode("separate");
    setSharedUniverseConfig(DEFAULT_CONFIG);
    setCommittedState(null);
    setSelves(null);
    setExploringIndex(null);
    setCompareSelection([]);
    setUrlNotice(null);
    setFeedback(null);
    setGenerationError(null);
    setGenerationErrorDetail(null);
    setCopyFallbackText(null);
    setShareFallbackUrl(null);
    resetAddressBar();
  }, [resetAddressBar]);

  // Surprise Me: populate random answers AND universe mode (about 40% shared), keep typed nickname, generate immediately
  const handleSurpriseMe = useCallback(() => {
    const randomized = randomInput();
    const currentNickname = sanitizeNickname(formInput.nickname).value;
    const nextInput: SelvesInput = {
      ...randomized,
      nickname: currentNickname
    };

    // ~40% shared universe with a random cosmos, else independent
    const isShared = Math.random() < 0.4;
    const newUniverseMode = isShared ? "shared" : "separate";
    const newSharedConfig = isShared ? randomConfig(Math.random) : sharedUniverseConfig;
    if (isShared) {
      setStoredRandomUniverse(newSharedConfig);
      setSharedUniverseConfig(newSharedConfig);
    }
    setUniverseMode(newUniverseMode);
    setFormInput(nextInput);
    const freshVariants = randomVariants();
    const nextState: SelvesState = {
      input: nextInput,
      variants: freshVariants,
      universe: isShared ? newSharedConfig : null
    };
    runGeneration("surprise-me", () => {
      const computed = generateSelves(nextState);
      setCommittedState(nextState);
      setSelves(computed);
      setExploringIndex(null);
      setCompareSelection([]);
      setCopyFallbackText(null);
      setShareFallbackUrl(null);

      // Non-essential extras wrapped safely
      safely("handleSurpriseMe.recordToLibrary", () => {
        recordToLibrary(nextState);
      });
      safely("handleSurpriseMe.updateAddressBar", () => {
        updateAddressBar(nextState);
      });
      safely("handleSurpriseMe.feedback", () => {
        showFeedback("Populated random exploratory traits & generated 4 new timelines", true);
      });
      scrollToElementAfterRender(() => resultsRef.current, {
        block: "start",
        onlyIfNeeded: true
      });
    });
  }, [formInput.nickname, sharedUniverseConfig, runGeneration, recordToLibrary, updateAddressBar, showFeedback]);

  // Clear questionnaire answers
  const handleClearAnswers = useCallback(() => {
    setGenerationError(null);
    setGenerationErrorDetail(null);
    setFormInput(prev => ({
      nickname: prev.nickname,
      interests: [],
      strengths: [],
      ambition: null,
      lifePath: null
    }));
  }, []);

  // Toggle Explore drawer / panel
  const handleToggleExplore = useCallback((index: number) => {
    let opening = false;
    setExploringIndex(prev => {
      const next = prev === index ? null : index;
      if (next !== null) {
        opening = true;
      }
      return next;
    });
    if (opening) {
      scrollToElementAfterRender(() => detailRef.current, {
        block: "nearest",
        onlyIfNeeded: true
      });
    }
  }, []);

  // Toggle Compare selection (max 2 items)
  const handleToggleCompare = useCallback((index: number) => {
    let nowComparing = false;
    setCompareSelection(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      }
      if (prev.length < 2) {
        const next = [...prev, index];
        if (next.length === 2) {
          nowComparing = true;
        }
        return next;
      }
      // Replaces oldest item
      nowComparing = true;
      return [prev[1], index];
    });
    if (nowComparing) {
      scrollToElementAfterRender(() => compareRef.current, {
        block: "nearest",
        onlyIfNeeded: true
      });
    }
  }, []);

  // Swap compared selves in CompareView
  const handleSwapCompared = useCallback(() => {
    setCompareSelection(prev => {
      if (prev.length === 2) {
        return [prev[1], prev[0]];
      }
      return prev;
    });
  }, []);

  // Copy single self profile
  const handleCopySelf = useCallback(async (self: AlternateSelf) => {
    if (!committedState) return;
    const shareUrl = getShareUrl(committedState);
    const text = buildSelfShareText(self, committedState.input, shareUrl);
    const ok = await copyText(text);
    if (ok) {
      showFeedback(`Copied ${self.name}'s profile to clipboard`, true);
      setCopyFallbackText(null);
    } else {
      showFeedback("Couldn't copy automatically — please copy from the text box below", false);
      setCopyFallbackText(text);
    }
  }, [committedState, getShareUrl, showFeedback]);

  // Copy all four profiles
  const handleCopyAll = useCallback(async () => {
    if (!committedState || !selves) return;
    const shareUrl = getShareUrl(committedState);
    const text = buildAllShareText(selves, committedState.input, shareUrl);
    const ok = await copyText(text);
    if (ok) {
      showFeedback("Copied all four alternate self profiles to clipboard", true);
      setCopyFallbackText(null);
    } else {
      showFeedback("Couldn't copy automatically — please copy from the text box below", false);
      setCopyFallbackText(text);
    }
  }, [committedState, selves, getShareUrl, showFeedback]);

  // Download PDF
  const handleDownloadPdf = useCallback(async () => {
    if (!committedState || !selves) return;
    setPdfGenerating(true);
    setPdfError(null);
    try {
      const pdfModule = await import("../pdf/downloadDossierPdf");
      await (pdfModule.downloadDossierPdf as any)({
        state: committedState,
        selves,
        // Full unredacted selves
        reportId: currentVariationId || "MLD-FREE",
        bridgeChapter: getDossierChapter()
      });
      showFeedback("Multiverse Life Dossier PDF downloaded successfully", true);
    } catch (err: any) {
      setPdfError(err?.message || "Failed to generate dossier PDF. Please retry.");
      showFeedback("Failed to download PDF", false);
    } finally {
      setPdfGenerating(false);
    }
  }, [committedState, selves, currentVariationId, showFeedback]);

  // Free access: all selves display unredacted
  const displaySelves = selves;

  // Current report code for SavedPossibilities
  const currentReportCode = useMemo(() => {
    if (!committedState) return "";
    return encodeState(committedState);
  }, [committedState]);

  // Dream Bridge integration
  const bridgePlan = useBridgeStore(s => s.plan);
  const includeInDossier = useBridgeStore(s => s.bridge.includeInDossier);

  // Publish / clear persona summaries to Dream Bridge store
  useEffect(() => {
    if (!selves || selves.length === 0) {
      bridgeActions.clearPersonas();
      return;
    }
    const summaries = selves.map((s, idx) => {
      let topAxis: AxisId = AXIS_ORDER[0];
      let maxVal = -Infinity;
      for (const axis of AXIS_ORDER) {
        const val = s.axes[axis] ?? 0;
        if (val > maxVal) {
          maxVal = val;
          topAxis = axis;
        }
      }
      return {
        index: idx as 0 | 1 | 2 | 3,
        archetype: s.archetype,
        name: s.name,
        title: s.title,
        occupation: s.occupation,
        scenarioLabel: s.scenarioLabel,
        universeName: s.universe.name,
        topAxis,
        tagline: s.tagline
      };
    });
    bridgeActions.publishPersonas(summaries);
  }, [selves]);

  // Handle request to bridge a specific self
  const handleBridgeSelf = useCallback((index: number) => {
    if (index >= 0 && index <= 3) {
      bridgeActions.requestPersona(index as 0 | 1 | 2 | 3);
    }
  }, []);

  // Smooth / instant scroll to Dream Bridge section
  const handleScrollToBridge = useCallback(() => {
    if (typeof window === "undefined") return;
    const section = document.getElementById("dream-bridge");
    if (section) {
      scrollToElement(section, {
        block: "start"
      });
    }
  }, []);

  // Share Link
  const handleShareLink = useCallback(async () => {
    if (!committedState) return;
    const shareUrl = getShareUrl(committedState);
    updateAddressBar(committedState);

    // Try Web Share API first
    if (typeof navigator !== "undefined" && typeof (navigator as any).share === "function") {
      try {
        await (navigator as any).share({
          title: "Quantum Liaison — Your Infinite Selves",
          text: `Explore four fictional alternate selves for ${displayName(committedState.input)} across the multiverse.`,
          url: shareUrl
        });
        showFeedback("Shared multiverse link successfully", true);
        setShareFallbackUrl(null);
        return;
      } catch (err: unknown) {
        if ((err as {
          name?: string;
        })?.name === "AbortError") {
          return; // Silent on user cancellation
        }
      }
    }

    // Fall back to clipboard
    const ok = await copyText(shareUrl);
    if (ok) {
      showFeedback("Share link copied to clipboard", true);
      setShareFallbackUrl(null);
    } else {
      showFeedback("Couldn't copy link automatically — please select it below", false);
      setShareFallbackUrl(shareUrl);
    }
  }, [committedState, getShareUrl, updateAddressBar, showFeedback]);

  // Place all four selves in the specified universe (from SelfDetail)
  const handlePlaceAllInUniverse = useCallback((cfg: UniverseConfig) => {
    if (!committedState) return;
    runGeneration("place-all-in-universe", () => {
      const targetProfile = generateUniverse(cfg);
      const nextState: SelvesState = {
        ...committedState,
        universe: cfg
      };
      const computed = generateSelves(nextState);
      setUniverseMode("shared");
      setSharedUniverseConfig(cfg);
      setCommittedState(nextState);
      setSelves(computed);

      // Non-essential extras wrapped safely
      safely("handlePlaceAllInUniverse.recordToLibrary", () => {
        recordToLibrary(nextState);
      });
      safely("handlePlaceAllInUniverse.updateAddressBar", () => {
        updateAddressBar(nextState);
      });
      safely("handlePlaceAllInUniverse.feedback", () => {
        showFeedback(`All four selves now live in ${targetProfile.name}`, true);
      });
    });
  }, [committedState, runGeneration, recordToLibrary, updateAddressBar, showFeedback]);

  // Interest chip toggle
  const toggleInterest = useCallback((id: InterestId) => {
    setGenerationError(null);
    setGenerationErrorDetail(null);
    setFormInput(prev => {
      const exists = prev.interests.includes(id);
      if (exists) {
        return {
          ...prev,
          interests: prev.interests.filter(item => item !== id)
        };
      }
      if (prev.interests.length >= MAX_INTERESTS) {
        return prev;
      }
      const next = [...prev.interests, id];
      const ordered = INTERESTS.filter(opt => next.includes(opt.id)).map(opt => opt.id);
      return {
        ...prev,
        interests: ordered
      };
    });
  }, []);

  // Strength chip toggle
  const toggleStrength = useCallback((id: StrengthId) => {
    setGenerationError(null);
    setGenerationErrorDetail(null);
    setFormInput(prev => {
      const exists = prev.strengths.includes(id);
      if (exists) {
        return {
          ...prev,
          strengths: prev.strengths.filter(item => item !== id)
        };
      }
      if (prev.strengths.length >= MAX_STRENGTHS) {
        return prev;
      }
      const next = [...prev.strengths, id];
      const ordered = STRENGTHS.filter(opt => next.includes(opt.id)).map(opt => opt.id);
      return {
        ...prev,
        strengths: ordered
      };
    });
  }, []);

  // Ambition toggle (single-choice, re-click deselects)
  const toggleAmbition = useCallback((id: AmbitionId) => {
    setGenerationError(null);
    setGenerationErrorDetail(null);
    setFormInput(prev => ({
      ...prev,
      ambition: prev.ambition === id ? null : id
    }));
  }, []);

  // Life Path toggle (single-choice, re-click deselects)
  const toggleLifePath = useCallback((id: LifePathId) => {
    setGenerationError(null);
    setGenerationErrorDetail(null);
    setFormInput(prev => ({
      ...prev,
      lifePath: prev.lifePath === id ? null : id
    }));
  }, []);
  return <section id="selves-studio" data-nav="dark" className="relative w-full max-w-full overflow-hidden bg-transparent text-foreground py-14 sm:py-20 lg:py-28">
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full">
        {/* Section Heading & Tagline */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <SectionHeading eyebrow="One Identity · Infinite Possibilities" title="Meet Your Infinite Selves" description="Enter an optional nickname and questionnaire answers to synthesize four distinct alternate personas across hypothetical quantum timelines — each anchored in its own physical cosmos." align="center" as="h2" />

          {/* URL Notice Banner */}
          {urlNotice && <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-xs text-muted-foreground text-left shadow-md">
              <AlertTriangle className="size-4 text-chart-3 shrink-0" />
              <span>{urlNotice}</span>
            </div>}
        </div>

        {/* Mandatory Educational & Privacy Framing Notes Box */}
        <div className="max-w-4xl mx-auto mb-8 sm:mb-10 p-4 sm:p-5 rounded-xl border border-border/80 bg-card/80 shadow-lg space-y-3 text-xs text-muted-foreground backdrop-blur-md w-full">
          <div className="flex items-start gap-2.5 sm:gap-3">
            <Info className="size-4 text-primary shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <span className="font-semibold text-foreground">Multiverse Storytelling Notice: </span>
              <span className="break-words">{FICTION_NOTICE}</span>
            </div>
          </div>
          <div className="flex items-start gap-2.5 sm:gap-3 border-t border-border/40 pt-3">
            <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <span className="font-semibold text-foreground">Privacy Architecture: </span>
              <span className="break-words">{PRIVACY_NOTE}</span>
            </div>
          </div>
        </div>

        {/* Main Questionnaire Studio Form Card: Glass 'Timeline Console' */}
        <div className="ql-glass-strong max-w-4xl mx-auto rounded-2xl p-4 sm:p-6 md:p-10 shadow-2xl space-y-6 sm:space-y-8 border-primary/30 w-full">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border/60 pb-5">
            <div>
              <div className="flex items-center gap-2">
                <span className="size-2 rounded-full bg-primary animate-pulse shrink-0" />
                <h3 className="font-serif text-lg sm:text-xl md:text-2xl font-bold text-foreground break-words">
                  Identity & Horizon Tuning Console
                </h3>
              </div>
              <p className="text-xs text-muted-foreground mt-1 break-words">
                All parameters are optional. Skip any question, or press "Surprise me" to generate a random configuration straight away.
              </p>
            </div>
            <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
              <button type="button" onClick={handleSurpriseMe} className="ql-btn-ghost rounded-full text-xs min-h-[44px] px-3.5 sm:px-4 cursor-pointer">
                <Sparkles className="size-3.5 mr-1.5 text-primary shrink-0" />
                <span>Surprise me</span>
              </button>
              <button type="button" onClick={handleClearAnswers} className="min-h-[44px] px-3 sm:px-3.5 rounded-full text-xs font-mono text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                Clear answers
              </button>
            </div>
          </div>

          <div className="space-y-8">
            {/* 1. Nickname Field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="nickname-input" className="text-sm font-semibold text-foreground font-mono">
                  Your Nickname <span className="font-normal text-muted-foreground">(Optional)</span>
                </Label>
                <span className="font-mono text-[11px] text-muted-foreground">
                  {formInput.nickname.length} / {NICKNAME_MAX}
                </span>
              </div>
              <input id="nickname-input" type="text" maxLength={NICKNAME_MAX} value={formInput.nickname} onChange={e => {
              setGenerationError(null);
              setGenerationErrorDetail(null);
              setFormInput(prev => ({
                ...prev,
                nickname: e.target.value
              }));
            }} placeholder="e.g. Kai, Ada, Rowan, Sol..." aria-invalid={!isFormValid} aria-describedby={nicknameValidation.error ? "nickname-error" : "nickname-desc"} className="ql-input min-h-[46px] text-base sm:text-sm text-foreground placeholder:text-muted-foreground" />
              {nicknameValidation.error ? <p id="nickname-error" className="text-xs font-mono text-destructive flex items-center gap-1.5">
                  <AlertTriangle className="size-3.5 shrink-0" />
                  <span>{nicknameValidation.error}</span>
                </p> : <p id="nickname-desc" className="text-[11px] text-muted-foreground">
                  Appears naturally in timelines and narrative dialogue. Falls back to "Traveler" if left blank. Never use emails, URLs or contact details.
                </p>}
            </div>

            {/* 2. Interests Chip Toggles */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-semibold text-foreground">
                    Core Fields of Fascination <span className="font-normal text-muted-foreground">(Select up to 3)</span>
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Influences occupations, technological affinities, and worldview details.
                  </p>
                </div>
                <span className="font-mono text-xs text-primary font-semibold px-2.5 py-0.5 rounded-full bg-primary/10 border border-primary/30 shadow-[0_0_8px_var(--glow-primary)]">
                  {formInput.interests.length} / {MAX_INTERESTS}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                {INTERESTS.map(item => {
                const isSelected = formInput.interests.includes(item.id);
                const isMaxReached = formInput.interests.length >= MAX_INTERESTS && !isSelected;
                return <button key={item.id} type="button" disabled={isMaxReached} aria-pressed={isSelected} title={item.hint} onClick={() => toggleInterest(item.id)} data-active={isSelected} className={`ql-chip min-h-[44px] text-xs sm:text-sm px-4 py-2 ${isMaxReached ? "opacity-35 cursor-not-allowed" : ""}`}>
                      {isSelected && <Check className="size-3.5 shrink-0 stroke-[2.5]" />}
                      <span>{item.label}</span>
                    </button>;
              })}
              </div>
              {formInput.interests.length > 0 && <div className="pt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                  {INTERESTS.filter(opt => formInput.interests.includes(opt.id)).map(opt => <span key={opt.id}>
                      <strong className="text-foreground">{opt.label}:</strong> {opt.hint}
                    </span>)}
                </div>}
            </div>

            {/* 3. Strengths Chip Toggles */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-semibold text-foreground">
                    Signature Personal Strengths <span className="font-normal text-muted-foreground">(Select up to 3)</span>
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Shapes psychological traits, resilience patterns, and timeline divergences.
                  </p>
                </div>
                <span className="font-mono text-xs text-primary font-semibold px-2.5 py-0.5 rounded-full bg-primary/10 border border-primary/30 shadow-[0_0_8px_var(--glow-primary)]">
                  {formInput.strengths.length} / {MAX_STRENGTHS}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                {STRENGTHS.map(item => {
                const isSelected = formInput.strengths.includes(item.id);
                const isMaxReached = formInput.strengths.length >= MAX_STRENGTHS && !isSelected;
                return <button key={item.id} type="button" disabled={isMaxReached} aria-pressed={isSelected} title={item.hint} onClick={() => toggleStrength(item.id)} data-active={isSelected} className={`ql-chip min-h-[44px] text-xs sm:text-sm px-4 py-2 ${isMaxReached ? "opacity-35 cursor-not-allowed" : ""}`}>
                      {isSelected && <Check className="size-3.5 shrink-0 stroke-[2.5]" />}
                      <span>{item.label}</span>
                    </button>;
              })}
              </div>
              {formInput.strengths.length > 0 && <div className="pt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                  {STRENGTHS.filter(opt => formInput.strengths.includes(opt.id)).map(opt => <span key={opt.id}>
                      <strong className="text-foreground">{opt.label}:</strong> {opt.hint}
                    </span>)}
                </div>}
            </div>

            {/* 4. Single-Choice Grid: Ambition & Life Path */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
              {/* Ambition */}
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-semibold text-foreground">
                    Guiding Ambition <span className="font-normal text-muted-foreground">(Single choice, optional)</span>
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Drives the motives behind critical turning points.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {AMBITIONS.map(item => {
                  const isSelected = formInput.ambition === item.id;
                  return <button key={item.id} type="button" aria-pressed={isSelected} title={item.hint} onClick={() => toggleAmbition(item.id)} data-active={isSelected} className="ql-chip min-h-[44px] px-3 py-2 text-xs justify-center">
                        <span>{item.label}</span>
                      </button>;
                })}
                </div>
                {formInput.ambition && <p className="text-[11px] text-muted-foreground italic font-sans">
                    {AMBITIONS.find(a => a.id === formInput.ambition)?.hint}
                  </p>}
              </div>

              {/* Life Path */}
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-semibold text-foreground">
                    Instinctive Life Archetype <span className="font-normal text-muted-foreground">(Single choice, optional)</span>
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Anchors the baseline divergence and life choices.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {LIFE_PATHS.map(item => {
                  const isSelected = formInput.lifePath === item.id;
                  return <button key={item.id} type="button" aria-pressed={isSelected} title={item.hint} onClick={() => toggleLifePath(item.id)} data-active={isSelected} className="ql-chip min-h-[44px] px-3 py-2 text-xs justify-center">
                        <span>{item.label}</span>
                      </button>;
                })}
                </div>
                {formInput.lifePath && <p className="text-[11px] text-muted-foreground italic font-sans">
                    {LIFE_PATHS.find(l => l.id === formInput.lifePath)?.hint}
                  </p>}
              </div>
            </div>

            {/* 5. Universe Assignment Mode */}
            <div className="border-t border-border/70 pt-6 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <Label className="text-sm font-semibold text-foreground">
                    Multiverse Topology & Cosmological Ground
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Decide whether each alternate self occupies its own bespoke cosmos, or all four inhabit the same universal laws.
                  </p>
                </div>
                <Link to="/CreateYourUniversePage" className="text-xs text-primary hover:underline inline-flex items-center gap-1 font-mono font-medium">
                  <Compass className="size-3.5" />
                  <span>Design a custom universe</span>
                  <ArrowRight className="size-3" />
                </Link>
              </div>

              <RadioGroup value={universeMode} onValueChange={val => {
              setGenerationError(null);
              setGenerationErrorDetail(null);
              setUniverseMode(val as "separate" | "shared");
            }} className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                <div className={`p-4 rounded-xl border transition-all cursor-pointer ${universeMode === "separate" ? "bg-card border-primary ring-1 ring-primary/40 shadow-[0_0_16px_var(--glow-primary)]" : "bg-card/60 border-border hover:border-primary/40"}`} onClick={() => setUniverseMode("separate")}>
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value="separate" id="mode-separate" className="mt-1" />
                    <div>
                      <Label htmlFor="mode-separate" className="text-sm font-bold text-foreground cursor-pointer font-serif">
                        Independent Universes
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        Each timeline gets its own physics tuned to its archetype: human-scale physics for Divergent, deep stellar era for Ascendant, non-standard constants for Strange, and exotic curvature for Unexpected.
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-xl border transition-all cursor-pointer space-y-3 ${universeMode === "shared" ? "bg-card border-primary ring-1 ring-primary/40 shadow-[0_0_16px_var(--glow-primary)]" : "bg-card/60 border-border hover:border-primary/40"}`} onClick={() => setUniverseMode("shared")}>
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value="shared" id="mode-shared" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="mode-shared" className="text-sm font-bold text-foreground cursor-pointer font-serif">
                        Place All Four in One Universe
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        All four alternate personas co-exist under the exact same fundamental laws and cosmic fate.
                      </p>
                    </div>
                  </div>

                  {universeMode === "shared" && <div className="pt-2 border-t border-border/60 space-y-2">
                      <Label htmlFor="shared-universe-select" className="text-xs font-semibold text-foreground font-mono">
                        Select Cosmos:
                      </Label>
                      <Select value={customArrivalUniverse && configsEqual(sharedUniverseConfig, customArrivalUniverse.config) ? "custom" : configsEqual(sharedUniverseConfig, DEFAULT_CONFIG) ? "default" : "random"} onValueChange={val => {
                    setGenerationError(null);
                    setGenerationErrorDetail(null);
                    if (val === "default") {
                      setSharedUniverseConfig(DEFAULT_CONFIG);
                    } else if (val === "custom" && customArrivalUniverse) {
                      setSharedUniverseConfig(customArrivalUniverse.config);
                    } else if (val === "random") {
                      const rand = storedRandomUniverse || randomConfig(Math.random);
                      setStoredRandomUniverse(rand);
                      setSharedUniverseConfig(rand);
                    }
                  }}>
                        <SelectTrigger id="shared-universe-select" className="min-h-[44px] bg-background border-border text-foreground text-xs sm:text-sm">
                          <SelectValue placeholder="Choose a universe" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border text-foreground">
                          <SelectItem value="default">Our Universe (Standard Baseline Physics)</SelectItem>
                          <SelectItem value="random">
                            A Random Cosmos{" "}
                            {storedRandomUniverse ? `(${generateUniverse(storedRandomUniverse).name})` : "(Procedural)"}
                          </SelectItem>
                          {customArrivalUniverse && <SelectItem value="custom">
                              The Universe You Designed ({customArrivalUniverse.profile.name})
                            </SelectItem>}
                        </SelectContent>
                      </Select>

                      {currentSharedProfile && <div className="mt-2 p-3 rounded-lg bg-background/80 border border-border/60 text-xs space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-primary">{currentSharedProfile.name}</span>
                            <span className="font-mono text-[10px] text-muted-foreground">
                              {currentSharedProfile.code}
                            </span>
                          </div>
                          <p className="text-muted-foreground text-[11px] leading-relaxed">
                            {currentSharedProfile.habitabilityLabel} · Fate: {currentSharedProfile.fate}
                          </p>
                        </div>}
                    </div>}
                </div>
              </RadioGroup>
            </div>

            {/* Changed Settings Notice */}
            {hasSettingsChanged && <div className="rounded-xl border border-primary/40 bg-primary/10 p-4 text-xs text-foreground flex items-center gap-2.5 shadow-[0_0_12px_var(--glow-primary)]">
                <Sparkles className="size-4 text-primary shrink-0 animate-pulse" />
                <span>
                  Settings modified — select <strong>Reveal My Four Selves</strong> below to update your multiverse simulation.
                </span>
              </div>}

            {/* Generation Error Alert Box */}
            {generationError && <div role="alert" className="rounded-xl border border-destructive/50 bg-destructive/10 p-4 text-xs text-destructive flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-lg break-words">
                <div className="flex items-start gap-2.5 min-w-0">
                  <AlertTriangle className="size-4 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <span className="leading-relaxed break-words [overflow-wrap:anywhere]">{generationError}</span>
                    {generationErrorDetail && <span className="block mt-1 font-mono text-[11px] opacity-80 break-all [overflow-wrap:anywhere]">
                        Technical detail: {generationErrorDetail}
                      </span>}
                  </div>
                </div>
                <button type="button" disabled={!isFormValid || isGenerating} onClick={handleReveal} className="ql-btn min-h-[44px] min-w-0 sm:min-w-[120px] w-full sm:w-auto px-4 text-xs shrink-0 font-serif justify-center">
                  <span className="grid place-items-center">
                    <span className={`col-start-1 row-start-1 inline-flex items-center justify-center ${isGenerating ? "invisible" : ""}`}>
                      <span>Try again</span>
                    </span>
                    <span className={`col-start-1 row-start-1 inline-flex items-center justify-center ${isGenerating ? "" : "invisible"}`}>
                      <Loader2 className="size-3.5 mr-1.5 animate-spin motion-reduce:animate-none shrink-0" />
                      <span>Revealing…</span>
                    </span>
                  </span>
                </button>
              </div>}

            {/* Primary Action Button: Styled as ql-btn */}
            <div className="pt-2">
              <button type="button" disabled={!isFormValid || isGenerating} aria-busy={isGenerating} onClick={handleReveal} className="ql-btn w-full min-h-[50px] text-sm sm:text-base cursor-pointer justify-center">
                <span className="grid place-items-center">
                  <span className={`col-start-1 row-start-1 inline-flex items-center justify-center ${isGenerating ? "invisible" : ""}`}>
                    <Sparkles className="size-4 mr-1 shrink-0" />
                    <span>{selves ? "Update My Four Selves" : "Reveal My Four Selves"}</span>
                  </span>
                  <span className={`col-start-1 row-start-1 inline-flex items-center justify-center ${isGenerating ? "" : "invisible"}`}>
                    <Loader2 className="size-4 mr-1.5 animate-spin motion-reduce:animate-none shrink-0" />
                    <span>Revealing…</span>
                  </span>
                </span>
              </button>
            </div>

            {/* Optional · The Dream-to-Reality Bridge Card directly under Reveal */}
            <div className="rounded-xl border border-border/80 bg-card/60 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-4 shadow-sm">
              <div className="space-y-1 min-w-0">
                <span className="text-[10px] sm:text-xs font-mono uppercase tracking-wider text-primary font-bold block">
                  Optional · The Dream-to-Reality Bridge
                </span>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Curious what your imagined lives may say about what you want from real life? Explore a private, optional reflection and turn it into small real-world steps.
                </p>
              </div>
              <button type="button" onClick={handleScrollToBridge} className="ql-btn-ghost rounded-full min-h-[44px] text-xs font-serif shrink-0 px-4 justify-center">
                <Sparkles className="size-3.5 mr-1.5 text-primary shrink-0" />
                <span>Open the Bridge</span>
              </button>
            </div>
          </div>
        </div>

        {/* Saved Possibilities / Browser Library Component */}
        <SavedPossibilities currentCode={currentReportCode} onSelectCode={handleSelectCode} />

        {/* Global Feedback Region */}
        <div aria-live="polite" className="max-w-4xl mx-auto min-h-[28px] mt-4 px-2">
          {feedback && <p className={`text-xs font-mono flex items-center justify-center gap-2 ${feedback.success ? "text-primary" : "text-destructive"}`}>
              {feedback.success ? <Check className="size-4 shrink-0" /> : <AlertTriangle className="size-4 shrink-0" />}
              <span>{feedback.message}</span>
            </p>}
        </div>

        {/* Results Section */}
        <div ref={resultsRef} className="mt-14 space-y-10">
          <AnimatePresence mode="wait">
            {selves && committedState ? <motion.div key="results-block" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.4
          }} className="space-y-8">
                {/* Result Header & Global Actions Toolbar with Glass Persistent Fiction Notice */}
                <div className="ql-glass-strong border-primary/30 p-4 sm:p-7 shadow-2xl flex flex-col md:flex-row items-stretch md:items-center justify-between gap-5">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground break-words">
                        Timelines for {displayName(committedState.input)}
                      </h3>
                      <Badge variant="outline" className="text-xs font-mono border-primary/40 text-primary bg-primary/10">
                        Variation {currentVariationId} · engine {ENGINE_VERSION}
                      </Badge>
                    </div>
                    <div className="inline-flex items-center gap-2 rounded-2xl sm:rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs text-primary shadow-[0_0_8px_var(--glow-primary)]">
                      <ShieldAlert className="size-3.5 shrink-0" />
                      <span className="font-mono text-[10px] sm:text-[11px] break-words">
                        Fictional Multiverse Simulation — Pure Educational Thought Experiment
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:flex md:flex-wrap items-center gap-2.5 w-full md:w-auto">
                    <button type="button" onClick={handleExploreAnotherPossibility} className="ql-btn-ghost rounded-full text-xs min-h-[44px] px-3.5 sm:px-4 justify-center" aria-label="Explore Another Possibility">
                      <RotateCcw className="size-3.5 mr-1.5 shrink-0" />
                      <span>Explore Another Possibility</span>
                    </button>
                    <button type="button" onClick={handleCopyAll} className="ql-btn-ghost rounded-full text-xs min-h-[44px] px-3.5 sm:px-4 justify-center">
                      <Copy className="size-3.5 mr-1.5 shrink-0" />
                      <span>Copy all profiles</span>
                    </button>
                    <button type="button" onClick={handleShareLink} className="ql-btn rounded-full text-xs min-h-[44px] px-3.5 sm:px-4 justify-center">
                      <Share2 className="size-3.5 mr-1.5 shrink-0" />
                      <span>Share link</span>
                    </button>
                    <button type="button" onClick={handleStartOver} className="min-h-[44px] px-3.5 rounded-full text-xs font-mono text-muted-foreground hover:text-foreground transition-colors cursor-pointer justify-center flex items-center">
                      Start over
                    </button>
                  </div>
                </div>

                {/* Compare Bar Notice */}
                {compareSelection.length > 0 && <div className="p-3.5 rounded-xl border border-primary/40 bg-primary/10 flex flex-wrap items-center justify-between gap-3 text-xs shadow-[0_0_12px_var(--glow-primary)]">
                    <div className="flex items-center gap-2">
                      <Sparkles className="size-4 text-primary shrink-0" />
                      <span>
                        Comparing:{" "}
                        <strong className="text-foreground">
                          {selves[compareSelection[0]]?.name}
                        </strong>{" "}
                        {compareSelection.length === 2 && <>
                            and{" "}
                            <strong className="text-foreground">
                              {selves[compareSelection[1]]?.name}
                            </strong>
                          </>}
                        {compareSelection.length === 1 && <span className="text-muted-foreground"> (select one more self to compare side-by-side)</span>}
                      </span>
                    </div>
                    <button type="button" onClick={() => setCompareSelection([])} className="min-h-[44px] px-3.5 py-1.5 rounded-full text-xs font-mono text-muted-foreground hover:text-foreground cursor-pointer flex items-center">
                      Clear comparison
                    </button>
                  </div>}

                {/* Grid of 4 Alternate Selves Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 items-stretch">
                  {displaySelves && displaySelves.map((self, idx) => <SelfCard key={self.timelineId} self={self} index={idx} exploring={exploringIndex === idx} comparing={compareSelection.includes(idx)} compareDisabled={false} onExplore={() => handleToggleExplore(idx)} onToggleCompare={() => handleToggleCompare(idx)} onRegenerate={() => handleRegenerateCard(idx)} onCopy={() => handleCopySelf(selves[idx])} onBridge={() => handleBridgeSelf(idx)} />)}
                </div>

                {/* Compare View Panel (when 2 are selected) */}
                {compareSelection.length === 2 && displaySelves && <div ref={compareRef} className="pt-4">
                    <CompareView a={displaySelves[compareSelection[0]]} b={displaySelves[compareSelection[1]]} onSwap={handleSwapCompared} onClose={() => setCompareSelection([])} />
                  </div>}

                {/* Self Detail Drawer / Panel (when exploring one) */}
                {exploringIndex !== null && displaySelves && displaySelves[exploringIndex] && <div ref={detailRef} className="pt-4">
                    <SelfDetail self={displaySelves[exploringIndex]} others={displaySelves.filter((_, i) => i !== exploringIndex)} nickname={displayName(committedState.input)} onClose={() => setExploringIndex(null)} onRegenerate={() => handleRegenerateCard(exploringIndex)} onCopy={() => handleCopySelf(selves[exploringIndex])} onPlaceAllInUniverse={handlePlaceAllInUniverse} onBridge={() => handleBridgeSelf(exploringIndex)}
              // New props contract from batch spec
              allSelves={displaySelves} activeIndex={exploringIndex} onSwitchSelf={(newIdx: number) => handleToggleExplore(newIdx)} variationId={currentVariationId} />
                  </div>}

                {/* Dossier Panel */}
                {selves && committedState && <div className="ql-glass-strong rounded-2xl border border-primary/60 p-5 sm:p-8 md:p-10 shadow-2xl space-y-6 max-w-4xl mx-auto">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border/60 pb-5">
                      <div className="space-y-1">
                        <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs text-primary shadow-[0_0_10px_var(--glow-primary)]">
                          <span className="font-mono uppercase font-bold tracking-wider">
                            Full Dossier
                          </span>
                        </div>
                        <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
                          Multiverse Life Dossier Archive
                        </h3>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          All biographies, routines, standing, possible futures, and PDF downloads unlocked.
                        </p>
                      </div>
                    </div>

                    {/* Actions Group */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                      <button type="button" disabled={pdfGenerating} onClick={handleDownloadPdf} className="ql-btn min-h-[48px] text-xs sm:text-sm uppercase tracking-wider font-bold justify-center">
                        {pdfGenerating ? <>
                            <Sparkles className="size-4 mr-2 animate-spin shrink-0" />
                            <span>Building PDF…</span>
                          </> : <>
                            <Download className="size-4 mr-2 shrink-0" />
                            <span>Download PDF</span>
                          </>}
                      </button>

                      <button type="button" onClick={handleShareLink} className="ql-btn-ghost min-h-[48px] text-xs sm:text-sm uppercase tracking-wider font-bold justify-center">
                        <Share2 className="size-4 mr-2 shrink-0" />
                        <span>Share link</span>
                      </button>

                      <button type="button" onClick={handleCopyAll} className="ql-btn-ghost min-h-[48px] text-xs sm:text-sm uppercase tracking-wider font-bold justify-center">
                        <Copy className="size-4 mr-2 shrink-0" />
                        <span>Copy all profiles</span>
                      </button>
                    </div>

                    {/* From Imagined Self to Lived Reality Chapter Row */}
                    <div className="p-4 sm:p-5 rounded-xl border border-border/70 bg-card/70 space-y-2 mt-4">
                      {bridgePlan ? <div className="space-y-2">
                          <label className="flex items-start gap-3 cursor-pointer">
                            <Checkbox checked={includeInDossier} onCheckedChange={checked => bridgeActions.setIncludeInDossier(Boolean(checked))} className="mt-0.5" />
                            <div className="space-y-1 min-w-0">
                              <span className="font-semibold text-xs sm:text-sm text-foreground block">
                                Include the chapter "From Imagined Self to Lived Reality" in my PDF
                              </span>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                Contains practical, self-directed suggestions based on your chosen themes and aspirations. It is separate from the fictional storytelling and is not therapy.
                              </p>
                            </div>
                          </label>
                        </div> : <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="space-y-0.5">
                            <span className="font-semibold text-xs sm:text-sm text-foreground block">
                              Add a practical reflection chapter to your PDF
                            </span>
                            <p className="text-xs text-muted-foreground">
                              Finish the Dream-to-Reality Bridge to add an optional practical chapter.
                            </p>
                          </div>
                          <button type="button" onClick={handleScrollToBridge} className="ql-btn-ghost min-h-[44px] text-xs rounded-full px-4 shrink-0 justify-center">
                            <Sparkles className="size-3.5 mr-1.5 text-primary shrink-0" />
                            <span>Go to the Bridge</span>
                          </button>
                        </div>}
                    </div>

                    {/* PDF Error Display */}
                    {pdfError && <div className="p-3.5 rounded-xl border border-destructive/50 bg-destructive/10 text-destructive text-xs font-mono flex items-center gap-2.5">
                        <AlertTriangle className="size-4 shrink-0" />
                        <span>{pdfError}</span>
                      </div>}
                  </div>}

                {/* Fallback Textarea for Manual Text Copying */}
                {copyFallbackText && <div className="p-4 rounded-xl border border-border bg-card space-y-2">
                    <Label htmlFor="manual-copy-area" className="text-xs font-semibold text-foreground font-mono">
                      Manual Text Copy Area:
                    </Label>
                    <textarea id="manual-copy-area" readOnly value={copyFallbackText} onFocus={e => e.target.select()} rows={6} className="ql-input font-mono text-base sm:text-xs" />
                  </div>}

                {/* Fallback Input for Manual Share Link */}
                {shareFallbackUrl && <div className="p-4 rounded-xl border border-border bg-card space-y-2">
                    <Label htmlFor="manual-share-input" className="text-xs font-semibold text-foreground font-mono">
                      Manual Share Link:
                    </Label>
                    <input id="manual-share-input" type="text" readOnly value={shareFallbackUrl} onFocus={e => e.target.select()} className="ql-input font-mono text-base sm:text-xs" />
                  </div>}
              </motion.div> : <motion.div key="empty-block" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }}>
                <div className="ql-glass max-w-2xl mx-auto p-8 sm:p-14 text-center space-y-5 border-border shadow-2xl">
                  <div className="size-16 rounded-full bg-primary/10 border border-primary/40 flex items-center justify-center mx-auto text-primary shadow-[0_0_16px_var(--glow-primary)]">
                    <Sparkles className="size-8" />
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground">
                    Four Versions of You Are Waiting
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed max-w-lg mx-auto">
                    Tune your nickname and questionnaire parameters in the horizon matrix above, then select{" "}
                    <strong>Reveal My Four Selves</strong> to calculate your alternate paths across the multiverse.
                  </p>
                  <div className="pt-2">
                    <button type="button" disabled={!isFormValid || isGenerating} aria-busy={isGenerating} onClick={handleReveal} className="ql-btn min-h-[46px] min-w-0 sm:min-w-[210px] w-full sm:w-auto px-6 text-xs sm:text-sm cursor-pointer justify-center">
                      <span className="grid place-items-center">
                        <span className={`col-start-1 row-start-1 inline-flex items-center justify-center ${isGenerating ? "invisible" : ""}`}>
                          <Sparkles className="size-4 mr-1.5 shrink-0" />
                          <span>Reveal My Four Selves</span>
                        </span>
                        <span className={`col-start-1 row-start-1 inline-flex items-center justify-center ${isGenerating ? "" : "invisible"}`}>
                          <Loader2 className="size-4 mr-1.5 animate-spin motion-reduce:animate-none shrink-0" />
                          <span>Revealing…</span>
                        </span>
                      </span>
                    </button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Bottom spacing */}
    </section>;
}